import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useMemo, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { isPackageableCard } from '../../../../../components/quotes/applicationToPackage'
import {
  applicationToQuoteCards,
  indicationsToOfferCards,
} from '../../../../../components/quotes/applicationToQuoteCards'
import {
  getTotalPremium,
  type PricedQuoteCard,
  type QuoteCardData,
  QuoteCardStage,
} from '../../../../../components/quotes/quoteCardTypes'
import { coveragesAreSeparatelyPurchasable } from '../../../../../components/quotes/quoteCartItems'
import { CacheKeys } from '../../../../../lib/cache'
import { parseError } from '../../../../../lib/errors'
import { formatCurrency } from '../../../../../lib/utils'
import type { InsuranceApplication } from '../../../../../models/insuranceApplication'
import { COVERAGE_TYPE_LABELS } from '../../../../../models/insuranceCoverage'

interface PackageOption {
  /** Every offer this checkbox adds: one, or a card's whole set where the card sells them only together. */
  indicationIds: string[]
  carrierName: string
  /** The paper, so two of one brand's papers pricing the same coverage can be told apart. */
  carrierProgram?: string | null
  coverageLabels: string
  premium: number
}

function toOption(offers: PricedQuoteCard[]): PackageOption {
  return {
    indicationIds: offers.map((offer) => offer.id),
    carrierName: offers[0].carrierName,
    carrierProgram: offers[0].carrierProgram,
    coverageLabels: offers
      .flatMap((offer) => offer.coverages)
      .map((coverage) => COVERAGE_TYPE_LABELS[coverage.type])
      .join(', '),
    // Each offer's own total, fees and one-sum carriers included: what the package will charge.
    premium: offers.reduce((sum, offer) => sum + getTotalPremium(offer), 0),
  }
}

/**
 * One option per thing the customer's card lets them add: a coverage where it itemizes, else the card.
 * Takes each indication's own card and the page's merged cards.
 */
export function packageOptions(offers: QuoteCardData[], cards: QuoteCardData[]): PackageOption[] {
  const offersById = new Map(offers.map((offer) => [offer.id, offer]))
  return cards.flatMap((card) => {
    // Only what the customer could check out today, or saving would leave them no package at all.
    if (!isPackageableCard(card)) return []
    // Every line the card prices, picked or blocked ones too: they decide whether the card sells whole.
    const pricedOffers = [...new Set(card.coverages.map((coverage) => coverage.indicationId ?? card.id))].map(
      (indicationId) => offersById.get(indicationId)
    )
    const openOffers = pricedOffers.filter(
      // A superseded price is a canceled round the backend won't package.
      (offer): offer is PricedQuoteCard =>
        !!offer &&
        isPackageableCard(offer) &&
        !offer.appliedAt &&
        !offer.supersededAt &&
        !!offer.coverages.length
    )
    if (!openOffers.length) return []
    // Split only where every priced line itemizes on its own card, the same rule the package service enforces.
    const itemized =
      coveragesAreSeparatelyPurchasable(card) &&
      pricedOffers.every((offer) => offer?.stage === QuoteCardStage.PRICED && !!offer.itemizedCheckoutStyle)
    if (itemized) return openOffers.map((offer) => toOption([offer]))
    // A card sold whole can't be packaged once any of its priced lines is picked or blocked.
    return openOffers.length < pricedOffers.length ? [] : [toOption(openOffers)]
  })
}

interface CreatePackageDialogProps {
  orgId: string
  application: InsuranceApplication
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreatePackageDialog({ orgId, application, open, onOpenChange }: CreatePackageDialogProps) {
  const queryClient = useQueryClient()
  // Terms always block, loaded or not: the package service refuses an offer still waiting on them.
  const options = useMemo(
    () =>
      packageOptions(
        indicationsToOfferCards(application.indications),
        applicationToQuoteCards(application.indications)
      ),
    [application.indications]
  )
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set())
  // Only what is still listed: a refetch, or the carrier terms loading, can drop an option that was ticked.
  const pickedIds = options
    .flatMap((option) => option.indicationIds)
    .filter((indicationId) => picked.has(indicationId))

  const mutation = useMutation({
    mutationFn: (indicationIds: string[]) =>
      axios.post(`/api/v1/insure/organisations/${orgId}/packages/`, { indication_ids: indicationIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION, application.id] })
      onOpenChange(false)
    },
  })

  function toggle(option: PackageOption, checked: boolean) {
    setPicked((current) => {
      const next = new Set(current)
      option.indicationIds.forEach((indicationId) =>
        checked ? next.add(indicationId) : next.delete(indicationId)
      )
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create package</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {application.package && (
            <Banner variant="warning" small>
              This replaces the customer&apos;s current package.
            </Banner>
          )}
          {options.length === 0 ? (
            <p className="text-muted-foreground text-sm">No priced offers to put in a package yet.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {options.map((option) => (
                <div key={option.indicationIds.join(':')} className="flex items-start gap-3">
                  <Checkbox
                    id={`package-${option.indicationIds[0]}`}
                    checked={option.indicationIds.every((indicationId) => picked.has(indicationId))}
                    onCheckedChange={(checked) => toggle(option, checked === true)}
                  />
                  <Label
                    htmlFor={`package-${option.indicationIds[0]}`}
                    className="flex flex-1 flex-col items-start gap-0.5"
                  >
                    <span className="text-foreground text-sm font-semibold">
                      {option.carrierProgram
                        ? `${option.carrierName} · ${option.carrierProgram}`
                        : option.carrierName}
                    </span>
                    <span className="text-muted-foreground text-xs font-normal">
                      {option.coverageLabels} · {formatCurrency(option.premium)}
                    </span>
                  </Label>
                </div>
              ))}
            </div>
          )}
          {mutation.isError && (
            <Banner variant="error" small>
              {parseError(mutation.error)}
            </Banner>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={pickedIds.length === 0}
            loading={mutation.isPending ? 'Creating…' : undefined}
            onClick={() => mutation.mutate(pickedIds)}
          >
            Create package
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
